import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Switch,
  Button,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import {
  useGetPaymentCsvMutation,
  useLazyGetRefundRequestsQuery,
  useUpdateRefundStatusMutation,
} from "../../services/main";
import { Loader, showError, showToast } from "../../constants";
import { downloadCsv } from "../../utils/exportCsv";
import SkeletonTable from "../../components/SkeletonTable";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { Payments } from "../../types/General";
import Pagination from "../../components/Pagination";
import useAuth from "../../hooks/useAuth";
import useTranslation from "../../hooks/Translation";
import moment from "moment";
import { REFUND_STATUS } from "../../types/General";
import { formatPhoneLtr } from "../../utils/phoneDisplay";
import NoDataFound from "../../components/NoDataFound";
import {
  PERMISSION_MODULES,
  canShowTableActions,
  getPagePermissionFlags,
} from "../../constants/permissionModules";

const RefundRequests = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [getRefundReq, { isLoading }] = useLazyGetRefundRequestsQuery();
  const [updateRefundStatus] = useUpdateRefundStatusMutation();
  const [paymentCsv] = useGetPaymentCsvMutation();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [allRequests, setAllRequests] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const translate = useTranslation();
  const [hidePermission, setHidePermission] = useState({
    isAdd: true,
    isDelete: true,
    isView: true,
  });
  const [loading, setLoading] = useState(true);

  const checkPermission = () => {
    setHidePermission(
      getPagePermissionFlags(
        userData?.permission,
        PERMISSION_MODULES.REFUND_REQUESTS,
        userData?.role === "ADMIN",
      ),
    );
  };

  useEffect(() => {
    if (userData && userData?.permission?.length) {
      checkPermission();
    }
  }, [userData]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };
  let totalPages = Math.ceil(totalCount / 10);

  const getUserCsv = async () => {
    try {
      const response = await paymentCsv({
        type: "user",
        query: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        await downloadCsv(response?.data, "refunds.csv");
      } else {
        showError(translate?.error?.exportFailed || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || translate?.error?.exportFailed || "");
    }
  };

  const getRequests = async () => {
    setLoading(true);
    try {
      // Build request params - only include status if filter is not "all"
      const requestParams: any = {
        query: debouncedSearchTerm.trim() || "",
        page: page,
      };
      
      // Only add status parameter when a specific filter is selected
      if (statusFilter !== "all") {
        requestParams.status = Number(statusFilter);
      }
      if (startDate) requestParams.startDate = startDate;
      if (endDate) requestParams.endDate = endDate;
      
      const response = await getRefundReq(requestParams).unwrap();
      if (response?.statusCode === 200) {
        setLoading(false);
        setAllRequests(response?.data?.data || []);
        setTotalCount(response?.data?.total);
      } else {
        setLoading(false);
        setAllRequests([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as string);
    setPage(1); // Reset to first page when filter changes
  };

  const handleRefundStatusChange = async (
    event: SelectChangeEvent,
    refundId: string
  ) => {
    const selectedStatus = event.target.value as string;
    if (selectedStatus === "refund") {
      try {
        const data = {
          status: REFUND_STATUS.REFUNDED, // Send 2 (enum value)
        };
        const encryptedBody = generateEncryptedKeyBody(data) as CommonBody;
        const response = await updateRefundStatus({
          id: refundId,
          encryptedBody,
        }).unwrap();
        if (response?.statusCode === 200) {
          showToast(translate?.refunds?.statusUpdated || "");
          getRequests(); // Refresh the list
        } else {
          showError(response?.message || translate?.refunds?.statusUpdateFailed || "");
        }
      } catch (error: any) {
        showError(error?.data?.message || translate?.refunds?.updateError || "");
      }
    }
  };

  useEffect(() => {
    getRequests();
  }, [debouncedSearchTerm, page, statusFilter, startDate, endDate]);

  return (
    <MainContainer>
      <Loader isLoad={loading} isLinear />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{translate?.refunds?.manageRefunds}</h1>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <Box className="cards_header_right">
              <FormControl sx={{ minWidth: 200 }}>
                <Typography className="custom_label" sx={{ mb: 1 }}>
                  {translate?.refunds?.filter}
                </Typography>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  displayEmpty
                  className="select_div"
                  sx={{ height: "40px" }}
                >
                  <MenuItem value="all">{translate?.main?.All}</MenuItem>
                  <MenuItem value={String(REFUND_STATUS.PENDING)}>
                    {translate?.refunds?.pending}
                  </MenuItem>
                  <MenuItem value={String(REFUND_STATUS.REFUNDED)}>
                    {translate?.refunds?.refunded}
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150, ml: 2 }}>
                <Typography className="custom_label" sx={{ mb: 1 }}>
                  Start Date
                </Typography>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                  className="select_div"
                  style={{ height: "40px", padding: "0 10px", border: "1px solid #ccc", borderRadius: "4px" }}
                />
              </FormControl>
              <FormControl sx={{ minWidth: 150, ml: 2 }}>
                <Typography className="custom_label" sx={{ mb: 1 }}>
                  End Date
                </Typography>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  className="select_div"
                  style={{ height: "40px", padding: "0 10px", border: "1px solid #ccc", borderRadius: "4px" }}
                />
              </FormControl>
              {/* <Button className="btn btn_primary" onClick={getUserCsv}>
                <FileDownloadIcon /> {translate?.dashboard?.ExportCsv}
              </Button> */}
            </Box>
          </Box>
          <TableContainer className="table_container">
            <Box className="heading"></Box>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{translate?.main?.SNo}</TableCell>
                  <TableCell>Request ID</TableCell>
                  <TableCell>Orig. Request ID</TableCell>
                  <TableCell>{translate?.main?.propertyName}</TableCell>
                  <TableCell>{translate?.refunds?.refundedAmount}</TableCell>
                  <TableCell>{translate?.refunds?.dateOfRequest}</TableCell>
                  <TableCell>Refunded Date</TableCell>
                  <TableCell>{translate?.refunds?.userName}</TableCell>
                  <TableCell>{translate?.refunds?.userEmail}</TableCell>
                  <TableCell>{translate?.refunds?.userPhone}</TableCell>
                  <TableCell>{translate?.refunds?.refundStatus}</TableCell>
                  {canShowTableActions(hidePermission) ? <TableCell>Action</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonTable columns={11} rows={10} />
                ) : allRequests?.length > 0
                  ? allRequests?.map((row: any, i: any) => (
                      <TableRow key={row?._id}>
                        <TableCell align="center">
                          {(page - 1) * 10 + i + 1}
                        </TableCell>
                        <TableCell>{row?._id}</TableCell>
                        <TableCell>{row?.propertyDetail?._id || "-"}</TableCell>
                        <TableCell>
                          {row?.propertyDetail?.name || "-"}
                        </TableCell>
                        <TableCell>
                          {row?.refundedAmount
                            ? `${row.refundedAmount} ${translate?.main?.kd}`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row?.createdAt
                            ? moment(row?.createdAt).format("DD/MM/YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row?.refundedAt
                            ? moment(row?.refundedAt).format("DD/MM/YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row?.userDetail?.fullName || "-"}
                        </TableCell>
                        <TableCell>{row?.userDetail?.email || "-"}</TableCell>
                        <TableCell>
                          {formatPhoneLtr(
                            row?.userDetail?.countryCode,
                            row?.userDetail?.phone
                          )}
                        </TableCell>
                        <TableCell>
                          {row?.status === REFUND_STATUS.PENDING ? (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value="pending"
                                disabled={!hidePermission?.isAdd}
                                onChange={(e) =>
                                  handleRefundStatusChange(e, row?._id)
                                }
                                displayEmpty
                                sx={{
                                  "& .MuiSelect-select": {
                                    padding: "0",
                                    paddingRight: "20px !important",
                                    fontSize: "inherit",
                                    fontFamily: "inherit",
                                    fontWeight: "inherit",
                                    color: "inherit",
                                    border: "none",
                                    background: "transparent",
                                    lineHeight: "inherit",
                                  },
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  boxShadow: "none",
                                  "&:hover": {
                                    boxShadow: "none",
                                  },
                                  height: "auto",
                                  minHeight: "auto",
                                }}
                              >
                                <MenuItem value="pending" disabled>
                                  {translate?.refunds?.pending}
                                </MenuItem>
                                <MenuItem value="refund">
                                  {translate?.refunds?.refundAction}
                                </MenuItem>
                              </Select>
                            </FormControl>
                          ) : row?.status === REFUND_STATUS.REFUNDED ? (
                            translate?.refunds?.refunded
                          ) : (
                            translate?.refunds?.cancelled
                          )}
                        </TableCell>
                        {canShowTableActions(hidePermission) ? (
                          <TableCell>
                            <IconButton onClick={() => navigate(`/refund-requests/details/${row?._id}`)}>
                              <VisibilityIcon />
                            </IconButton>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))
                  : (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <NoDataFound
                            message={translate?.refunds?.noRefundsFound}
                          />
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {allRequests?.length > 0 ? (
          <Pagination
            setPage={setPage}
            module={allRequests}
            page={page}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        ) : (
          ""
        )}
      </div>
    </MainContainer>
  );
};

export default RefundRequests;
